import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import TetfundLogo from '../../assets/images/TetfundLogo.svg';
import { useAuth } from '../hooks/auth';
import { Eye, EyeOff, ArrowLeft, BookOpen } from 'lucide-react';


interface LoginFormData {
  email: string;
  password: string;
  persistLogin: boolean;
}

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    persistLogin: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password, formData.persistLogin);
      navigate('/');
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const inputClass = "w-full bg-[#F3F4F6] border-none rounded-md py-3 px-4 text-gray-700 focus:ring-2 focus:ring-[#088A61] outline-none transition-all";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="flex min-h-screen font-sans bg-white">
      <div className="hidden lg:flex lg:w-1/2 bg-[#088A61] flex-col justify-between p-16 text-white sticky top-0 h-screen">
        <div>
          <div className="flex items-center gap-3">
            <div className="bg-white p-2 rounded-md">
              <img src={TetfundLogo} alt="Logo" className="h-8 w-auto" />
            </div>
            {/* <span className="text-xl font-medium">TAG Portal</span> */}
          </div>
        </div>

        <div className="max-w-xl">
          <h1 className="text-5xl font-normal leading-tight mb-8">
            Manuscript Submission for Academic Book Publication and Professional Journal Support


          </h1>
          <p className="text-lg opacity-90 leading-relaxed font-light text-center">

            <span className=" opacity-90 leading-relaxed font-bold text-center">Tetfund Tag System (TTS) v 1.0  </span>        </p>
        </div>

        <div className="text-sm opacity-70">
          © {new Date().getFullYear()} TETFund TAG. All rights reserved.
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 md:p-16 lg:p-24">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h2>
            <p className="text-gray-500">Enter your credentials to access your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className={labelClass}>Email address</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="name@example.com"
                value={formData.email}
                onChange={handleChange}
                className={inputClass}
                required
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="password" className={labelClass}>Password</label>
                <Link
                  to="/reset-password"
                  className="text-sm font-medium text-[#155DFC] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}

                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="persistLogin"
                name="persistLogin"
                checked={formData.persistLogin}
                onChange={handleChange}
                className="w-4 h-4 rounded border-gray-300 text-[#088A61] focus:ring-[#088A61]"
              />
              <label htmlFor="persistLogin" className="text-sm text-gray-600">
                Keep me logged in
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#088A61] text-white py-3.5 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-[#067451] transition-all disabled:bg-gray-400 mt-8"
            >
              {loading ? (
                'Signing in...'
              ) : (
                <>Sign in <span className="text-xl">→</span></>
              )}
            </button>

            <div className="text-center mt-8 space-y-4">
              <p className="text-gray-600">
                Don't have an account?{' '}
                <Link to="/signup" className="text-[#155DFC] font-semibold hover:underline">
                  Create account
                </Link>
              </p>

              <div className="pt-6 border-t border-gray-100">
                <Link
                  to="/library"
                  className="inline-flex items-center justify-center gap-2 w-full py-3 px-4 rounded-lg border-2 border-[#088A61] text-[#088A61] font-bold hover:bg-green-50 transition-all group"
                >
                  <BookOpen size={18} className="group-hover:scale-110 transition-transform" />
                  Browse Resource Library
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;